/**
 * Rewrites plain search queries into Shopify field-scoped queries so the
 * storefront returns only literal keyword matches instead of the semantic
 * matches that Shopify blends in (which cannot be disabled at store level
 * and ignore options[fields]).
 *
 * "athletic tee" becomes:
 * (title:athletic* OR product_type:athletic OR ... OR tag:athletics)
 * (title:tee* OR product_type:tee OR ... OR tag:tees)
 *
 * Groups are ANDed by Shopify, clauses inside a group are ORed, so every
 * word must match at least one field - the same rule as the
 * 'search-keyword-match' Liquid snippet, which stays in place as a safety
 * net for unscoped queries arriving from old links or third-party apps.
 *
 * Syntax rules verified against the storefront search:
 * - Prefix wildcards (*) are only reliable on title:. A wildcard on tag:
 *   makes Shopify fall back to loose matching (most of the catalogue).
 * - variants.title is valid in options[fields] but is a dead scope in
 *   query syntax (always 0 results), so it is not used here.
 * - The first clause of each group must be title:<original word>* -
 *   the 'search-display-terms' snippet reads the words back from there.
 */
(function () {
  var STOPWORDS = ['and', 'or', 'not'];
  var EXACT_FIELDS = ['product_type', 'vendor', 'tag'];
  var MAX_WORDS = 8;

  function normalize(str) {
    return str
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/['‘’]/g, '')
      .replace(/["“”.,:;\/\-_()\[\]+]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Original word first, then the singular/plural variants the Liquid
     keyword filter also accepts ("hoodies" -> "hoodie" and "hoody"). */
  function wordForms(word) {
    var forms = [word];
    if (word.length >= 4) {
      if (word.slice(-1) === 's') {
        forms.push(word.slice(0, -1));
        if (word.slice(-3) === 'ies') {
          forms.push(word.slice(0, -3) + 'y');
        }
      } else {
        forms.push(word + 's');
      }
    }
    return forms;
  }

  function buildScopedQuery(raw) {
    if (!raw) return raw;
    // Leave advanced queries (already scoped, quoted or wildcarded) untouched.
    if (/[:"*]/.test(raw)) return raw;

    var words = normalize(raw).split(' ').filter(function (word) {
      return word && STOPWORDS.indexOf(word) === -1;
    }).slice(0, MAX_WORDS);
    if (!words.length) return raw;

    var groups = words.map(function (word) {
      var forms = wordForms(word);
      var clauses = forms.map(function (form) {
        return 'title:' + form + '*';
      });
      forms.forEach(function (form) {
        EXACT_FIELDS.forEach(function (field) {
          clauses.push(field + ':' + form);
        });
      });
      return '(' + clauses.join(' OR ') + ')';
    });

    return groups.join(' ');
  }

  // Search form submits (header slideout, mobile menu). Navigate manually
  // instead of rewriting the q input: mutating the visible input flashes
  // the scoped query in the search box and browsers restore it on
  // back-navigation.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;
    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value) return;
    var scoped = buildScopedQuery(input.value);
    if (scoped === input.value) return;

    event.preventDefault();
    var params = new URLSearchParams();
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.disabled) return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      params.append(el.name, el.name === 'q' ? scoped : el.value);
    });
    window.location.assign(action + '?' + params.toString());
  }, true);

  // Links carrying a q param (predictive search suggestions, "view all",
  // clear-all links) - rewrite the href just before navigation.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var link = event.target.closest('a[href*="/search?"]');
    if (!link) return;
    try {
      var url = new URL(link.href, window.location.origin);
      var q = url.searchParams.get('q');
      if (!q) return;
      var scoped = buildScopedQuery(q);
      if (scoped === q) return;
      url.searchParams.set('q', scoped);
      link.href = url.toString();
    } catch (err) {
      // Malformed href - leave the link untouched.
    }
  }, true);

  window.searchQueryScope = { buildScopedQuery: buildScopedQuery };
})();
